export const TRAINING_LEVELS = [
  {
    key: 'zero',
    label: '我完全不会',
    description: '先看懂题目和交付要求，不急着写复杂功能。',
    advice: [
      '先把题目里的关键词圈出来，确认老师真正要交什么。',
      '只学本次任务会用到的 2-3 个基础概念。',
      '遇到不会的地方先写问题清单，再逐个查资料或问 AI。',
    ],
    firstTaskPrefix: '先用自己的话复述题目要求，并整理 5 个关键词',
  },
  {
    key: 'basic',
    label: '我会一点基础',
    description: '适合先做一个能跑通的简化版，再逐步补功能。',
    advice: [
      '先确定输入、处理、输出三件事。',
      '用最熟悉的技术做第一版，不追求复杂架构。',
      '每做完一步都截图或记录，后面可以直接写进报告。',
    ],
    firstTaskPrefix: '画出功能流程图，并完成第一个可运行小功能',
  },
  {
    key: 'code',
    label: '我会写简单代码',
    description: '可以按模块拆任务，先做核心流程，再补界面和报告。',
    advice: [
      '把需求拆成 3-5 个功能模块。',
      '先实现核心流程，再处理异常和美化。',
      '保留代码截图、运行截图和问题复盘。',
    ],
    firstTaskPrefix: '搭建项目结构，并完成核心模块的输入输出',
  },
  {
    key: 'project',
    label: '我已经做过项目',
    description: '重点放在需求边界、质量、报告表达和答辩材料。',
    advice: [
      '先确认评分点和交付清单。',
      '把功能、数据、界面、测试、报告分开推进。',
      '准备一页项目亮点和一页问题复盘。',
    ],
    firstTaskPrefix: '列出评分点和交付物清单，确定项目里程碑',
  },
]

const STACKS = [
  {
    test: /java|spring|后端|管理系统|mysql|数据库|web/i,
    stack: ['Java / Spring Boot 或 Node.js', 'MySQL 或 SQLite', 'HTML/CSS/React 或简单模板页', '接口测试工具'],
  },
  {
    test: /python|数据|分析|爬虫|可视化|机器学习|模型/i,
    stack: ['Python', 'pandas / openpyxl', 'matplotlib / ECharts', 'Jupyter 或普通脚本'],
  },
  {
    test: /网页|网站|前端|html|css|javascript|vue|react/i,
    stack: ['HTML/CSS/JavaScript', 'React 或 Vue', '浏览器调试工具', '本地静态页面或 Vite'],
  },
  {
    test: /单片机|物联网|传感器|嵌入式|arduino|stm32/i,
    stack: ['单片机开发板', '传感器模块', '串口调试工具', '数据记录表或简单可视化页面'],
  },
  {
    test: /电商|运营|新媒体|营销|策划|短视频|账号/i,
    stack: ['调研表格', '内容日历', '数据复盘表', 'PPT / 报告文档'],
  },
]

function splitSentences(input) {
  return String(input || '')
    .replace(/\r/g, '')
    .split(/[。；;\n]/)
    .map((item) => item.trim())
    .filter(Boolean)
}

function inferStack(text) {
  const matched = STACKS.find((item) => item.test.test(text))
  return matched?.stack || ['Excel / Word / PPT', '一个你最熟悉的实现工具', '截图记录', '报告文档']
}

function inferDeliverables(text) {
  const deliverables = ['一份实训/课程设计报告', '可运行或可展示的成果', '过程截图和结果截图']
  if (/代码|程序|系统|网站|小程序|平台|接口/i.test(text)) deliverables.unshift('项目源码或工程文件')
  if (/ppt|答辩|汇报/i.test(text)) deliverables.push('答辩 PPT')
  if (/数据|分析|问卷|报表/i.test(text)) deliverables.push('数据表、图表和分析结论')
  if (/视频|演示/i.test(text)) deliverables.push('演示视频或录屏')
  return Array.from(new Set(deliverables))
}

function inferGoal(text) {
  if (/管理系统|平台|网站|小程序|app/i.test(text)) return '做出一个能演示核心流程的小系统'
  if (/数据|分析|模型|预测|可视化/i.test(text)) return '完成数据处理、图表展示和结论说明'
  if (/策划|运营|营销|新媒体/i.test(text)) return '完成调研、方案、内容和复盘材料'
  if (/单片机|物联网|传感器|硬件/i.test(text)) return '完成硬件连接、数据采集和结果展示'
  return '理解要求，完成一个可提交、可解释、可展示的成果'
}

export function buildTrainingBreakdown(input, levelKey = 'zero') {
  const text = String(input || '').trim()
  const level = TRAINING_LEVELS.find((item) => item.key === levelKey) || TRAINING_LEVELS[0]
  const sentences = splitSentences(text)
  const goal = inferGoal(text)
  const deliverables = inferDeliverables(text)
  const stack = inferStack(text)
  const topic = sentences[0] || '老师布置的实训/课程设计'
  const steps = [
    {
      title: '第 1 步：读懂题目',
      detail: `把题目拆成“要做什么、给谁用、最后交什么”。当前可以先理解为：${goal}。`,
      action: level.key === 'zero' ? '先不要写代码，先写 5 行需求理解。' : '整理功能清单和交付清单。',
    },
    {
      title: '第 2 步：确定最小版本',
      detail: '只保留最核心流程，先做一个能演示的版本，再逐步补充。',
      action: '写出 3 个必须完成的功能，其他放到“有时间再做”。',
    },
    {
      title: '第 3 步：准备资料和工具',
      detail: `建议先用：${stack.join('、')}。不会的工具只学本次要用的部分。`,
      action: '建立文件夹，放题目、资料、截图、报告草稿。',
    },
    {
      title: '第 4 步：分模块完成',
      detail: '每完成一小步就保存截图和说明，后面写报告会轻松很多。',
      action: '按“输入-处理-输出”顺序推进，不要同时改太多地方。',
    },
    {
      title: '第 5 步：整理报告和答辩',
      detail: '报告重点写需求理解、实现过程、关键截图、遇到的问题和改进方向。',
      action: '最后一天不要只改代码，至少留半天整理报告。',
    },
  ]

  return {
    topic,
    goal,
    level,
    deliverables,
    stack,
    steps,
    pitfalls: [
      '一开始就追求完整大项目，最后核心流程跑不通。',
      '只贴代码，不解释为什么这样做，报告很空。',
      '没有截图和过程记录，最后补报告很痛苦。',
      '直接复制答案，答辩时讲不清楚，也不利于真正掌握。',
    ],
    reportContent: [
      '题目背景和需求理解',
      '系统/方案总体思路',
      '关键功能或关键步骤说明',
      '运行截图、数据图表或成果截图',
      '问题记录、解决过程和改进方向',
    ],
    todayTask: `${level.firstTaskPrefix}：${topic}`,
    antiCheat: '本工具帮助你理解要求、拆解步骤和规划学习路径，不提供可直接抄交的完整作业答案。',
  }
}
